import { useMountEffect } from "@/shared/lib/use-mount-effect";

const BAVARD_WORD_PATTERN = /\bBavard\b/g;
const SKIPPED_TEXT_SELECTOR = [
	".bavard-word",
	"[data-bavard-word]",
	"[data-bavard-word-ignore]",
	"code",
	"input",
	"kbd",
	"noscript",
	"option",
	"pre",
	"samp",
	"script",
	"select",
	"style",
	"svg",
	"textarea",
].join(",");

function containsBavardWord(value: string): boolean {
	BAVARD_WORD_PATTERN.lastIndex = 0;
	return BAVARD_WORD_PATTERN.test(value);
}

function shouldSkipTextNode(textNode: Text): boolean {
	const parent = textNode.parentElement;
	return !parent || parent.closest(SKIPPED_TEXT_SELECTOR) !== null;
}

function decorateTextNode(textNode: Text): void {
	const value = textNode.nodeValue ?? "";
	if (!(value && containsBavardWord(value)) || shouldSkipTextNode(textNode)) {
		return;
	}

	const fragment = document.createDocumentFragment();
	let cursor = 0;

	BAVARD_WORD_PATTERN.lastIndex = 0;
	for (const match of value.matchAll(BAVARD_WORD_PATTERN)) {
		const index = match.index ?? 0;
		if (index > cursor) {
			fragment.append(value.slice(cursor, index));
		}

		const span = document.createElement("span");
		span.className = "bavard-word";
		span.dataset.bavardWord = "true";
		span.textContent = match[0];
		fragment.append(span);

		cursor = index + match[0].length;
	}

	if (cursor < value.length) {
		fragment.append(value.slice(cursor));
	}

	textNode.replaceWith(fragment);
}

function collectMatchingTextNodes(root: Node): Text[] {
	if (root.nodeType === Node.TEXT_NODE) {
		const textNode = root as Text;
		return shouldSkipTextNode(textNode) ? [] : [textNode];
	}

	if (
		root.nodeType !== Node.ELEMENT_NODE &&
		root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
	) {
		return [];
	}

	if (
		root.nodeType === Node.ELEMENT_NODE &&
		(root as Element).closest(SKIPPED_TEXT_SELECTOR) !== null
	) {
		return [];
	}

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode(node) {
			const textNode = node as Text;
			if (
				shouldSkipTextNode(textNode) ||
				!containsBavardWord(textNode.nodeValue ?? "")
			) {
				return NodeFilter.FILTER_REJECT;
			}
			return NodeFilter.FILTER_ACCEPT;
		},
	});

	const textNodes: Text[] = [];
	let current = walker.nextNode();
	while (current) {
		textNodes.push(current as Text);
		current = walker.nextNode();
	}
	return textNodes;
}

function decorateSubtree(root: Node): void {
	for (const textNode of collectMatchingTextNodes(root)) {
		decorateTextNode(textNode);
	}
}

export function BavardWordDecorator() {
	useMountEffect(() => {
		if (
			typeof document === "undefined" ||
			typeof MutationObserver === "undefined"
		) {
			return;
		}

		const root = document.getElementById("root");
		if (!root) {
			return;
		}

		const pendingNodes = new Set<Node>();
		let scheduledFrame = 0;
		const scheduleFrame =
			typeof window.requestAnimationFrame === "function"
				? window.requestAnimationFrame.bind(window)
				: window.setTimeout.bind(window);
		const cancelFrame =
			typeof window.cancelAnimationFrame === "function"
				? window.cancelAnimationFrame.bind(window)
				: window.clearTimeout.bind(window);

		const flushPendingNodes = () => {
			scheduledFrame = 0;
			const nodes = Array.from(pendingNodes);
			pendingNodes.clear();
			for (const node of nodes) {
				decorateSubtree(node);
			}
		};

		const scheduleDecoration = (node: Node) => {
			pendingNodes.add(node);
			if (scheduledFrame === 0) {
				scheduledFrame = scheduleFrame(flushPendingNodes);
			}
		};

		decorateSubtree(root);

		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type === "characterData") {
					scheduleDecoration(mutation.target);
					continue;
				}

				for (const node of mutation.addedNodes) {
					scheduleDecoration(node);
				}
			}
		});

		observer.observe(root, {
			childList: true,
			characterData: true,
			subtree: true,
		});

		return () => {
			observer.disconnect();
			pendingNodes.clear();
			if (scheduledFrame !== 0) {
				cancelFrame(scheduledFrame);
			}
		};
	});

	return null;
}
